<template>
  <div class="min-h-full text-app-color flex flex-col bg-primary px-15 pb-20">
    <div class="w-full mx-auto mt-30 md:m-auto max-w-380">
      <div class="m-15">
        <form>
          <!-- FIRST STEP -->
          <div v-if="step === 1" key="step1" class="flex flex-col items-start">
            <div class="md:text-center w-full">
              <p class="text-24 md:text-30 font-bold">Welcome</p>
              <p class="mb-30">Sign up for hello ivy teams</p>
            </div>

            <div class="flex mb-30 mx-auto">
              <img v-if="image" class="rounded-6 h-115 max-w-full object-contain" :src="image" alt="image">

              <label v-else class="bg-grey-200 w-125 h-115 rounded-14 mx-auto border-3 border-dashed border-grey-700 flex opacity-90 hover:opacity-100 cursor-pointer">
                <p class="text-15 text-grey-700 m-auto">Upload logo</p>
                <input ref="inputRef" type="file" class="hidden" @change="uploadLogo">
              </label>
            </div>

            <InputModule
              ref="companyNameRef"
              v-model="companyName"
              name="companyName"
              label="Company name"
              placeholder="Acme inc."
              class="w-full mb-30 font-bold"
              input-classes="px-22 py-14"
              :error-message="errorMessages.companyName"
              @clearErrorMessage="name => errorMessages[name] = null"
            />

            <ButtonModule
              text="Continue"
              class="w-full justify-center bg-blue-new text-white font-bold rounded-4 mb-15 md:mb-20 py-20 px-67"
              @click.prevent="continueRegister"
            />

            <div class="md:mt-30 md:text-center w-full">
              <router-link :to="url" class="text-14 md:text-opacity-50 hover:text-opacity-100 mb-10 md:mb-35">
                Already have an account?
                <span class="font-bold text-blue-new">Log in</span>
              </router-link>
            </div>
          </div>

          <!-- SECOND STEP -->
          <div v-if="step === 2" key="step2" class="flex flex-col items-start">
            <div class="md:text-center w-full">
              <p class="text-24 md:text-30 font-bold">Hi {{ companyName }}</p>
              <p class="text-17">Let's set up your account, this won't take long.</p>
              <img v-if="image" class="rounded-6 h-115 max-w-full object-contain mt-30 mx-auto" :src="image" alt="image">
            </div>

            <InputModule
              v-model="team.workEmail"
              name="workEmail"
              type="email"
              label="Your work email"
              placeholder="email@example.com"
              class="w-full mb-30 mt-70 font-bold"
              input-classes="px-22 py-14"
              :error-message="errorMessages.workEmail"
              :is-need-link="errorMessages.isNeedLink"
              @clearErrorMessage="clearWorkEmailError"
            />

            <InputModule
              v-model="team.password"
              name="password"
              type="password"
              label="Set your password"
              placeholder="password"
              class="w-full mb-30 font-bold"
              input-classes="px-22 py-14"
              :error-message="errorMessages.password"
              @clearErrorMessage="name => errorMessages[name] = null"
            />

            <ButtonModule
              text="Next"
              class="w-full justify-center bg-blue-new text-white text-16 font-bold rounded-4 mb-30 py-20 px-46"
              @click.prevent="registerTeamSecondStep"
            />
          </div>

          <!-- THIRD  STEP -->
          <div v-if="step === 3" key="step3" class="flex flex-col items-start">
            <div class="md:text-center w-full">
              <p class="text-24 md:text-30 font-bold">Hi {{ companyName }}</p>
              <p class="text-17">Let's set up your account, <br> this won't take long…</p>
              <img v-if="image" class="rounded-6 h-115 max-w-full object-contain mt-30 mx-auto" :src="image" alt="image">
            </div>

            <InputModule
              v-model="account.firstName"
              name="firstName"
              label="Your first name"
              placeholder="Alex"
              class="w-full mb-30 mt-70 font-bold"
              input-classes="px-22 py-14"
              :error-message="errorMessages.firstName"
              @clearErrorMessage="name => errorMessages[name] = null"
            />

            <InputModule
              v-model="account.lastName"
              name="lastName"
              label="Your last name"
              placeholder="Johnson"
              class="w-full mb-30 font-bold"
              input-classes="px-22 py-14"
              :error-message="errorMessages.lastName"
              @clearErrorMessage="name => errorMessages[name] = null"
            />

            <ButtonModule
              text="Create account"
              class="w-full justify-center bg-blue-new text-white text-16 font-bold rounded-4 mb-30 py-20 px-46"
              @click.prevent="toStep4"
            />
          </div>

          <!--  FOURTH STEP -->
          <div v-if="step === 4" key="step4" class="flex flex-col items-start">
            <div class="md:text-center w-full">
              <p class="text-25 font-bold">Check your email for a code</p>
              <p class="text-17 mb-70">We've sent a 6-digit code to {{ team.workEmail }}.</p>
            </div>

            <InputModule
              v-model="confirmCode"
              type="number"
              name="confirmCode"
              label="Please enter code"
              placeholder="352846"
              class="w-full mb-30 font-bold"
              input-classes="px-22 py-14"
              :error-message="errorMessages.confirmCode"
              @keyup="checkConfirmCode"
            />

            <div class="text-14 pt-20 border-t border-hover mb-30 flex flex-col items-center w-full">
              <p>Can't find your code? Please check your spam folder.</p>
              <div>
                <ButtonModule
                  text="Click here"
                  class="text-blue-new mt-10"
                  :disabled="isDisabledCodeButton"
                  @click.prevent="sendVerificationCode"
                />

                <span>to receive a new code.</span>
              </div>
            </div>
          </div>

          <!-- INVITE TEAM MEMBERS -->
          <InviteTeamMembers v-if="step === 5" :team-id="teamId" :company-name="companyName" />

          <!-- LOGIN MODAL -->
          <LoginModal ref="loginModalRef" :email="team.workEmail" @on-login="onLogin" />
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useGtag } from 'vue-gtag-next'

import { teamsService, settingsService } from '@/services'

import InputModule from '@/components/shared/form-controls/InputModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import InviteTeamMembers from '@/components/auth/InviteTeamMembers'
import LoginModal from '@/components/auth/LoginModal'

import { requiredFieldsValidation, emailValidation, passwordValidation } from '@/helpers'

export default defineComponent({
  name: 'SignUpTeams',

  components: { InputModule, ButtonModule, InviteTeamMembers, LoginModal },

  setup () {
    const { commit, dispatch } = useStore()
    const route = useRoute()
    const router = useRouter()
    const { event } = useGtag()

    /* DATA */
    let verification = false
    const companyNameRef = ref('')
    const step = ref(1)
    const companyName = ref('')
    const confirmCode = ref('')
    const hasPersonalAccount = ref(false)
    const hasTeamAccount = ref(false)
    const isDisabledCodeButton = ref(false)
    const loginModalRef = ref(null)
    const teamId = ref(null)
    const signature = ref(null)
    const expire = ref(null)
    const isInvite = ref(null)
    const file = ref(null)
    const image = ref(null)

    const team = reactive({
      workEmail: '',
      password: ''
    })

    const account = reactive({
      firstName: '',
      lastName: ''
    })

    const errorMessages = reactive({
      companyName: null,
      workEmail: null,
      password: null,
      confirmCode: null,
      firstName: null,
      lastName: null,
      isNeedLink: false
    })

    /* COMPUTED */
    const url = computed(() => {
      return route.query ? { name: 'Login', query: route.query } : { name: 'Login' }
    })

    /* HOOKS */
    onMounted(() => {
      nextTick(() => {
        if (step.value === 1) {
          companyNameRef.value.focus()
        }
      })

      isInvite.value = route.query.invite
      if (isInvite.value) {
        step.value = 2
        team.workEmail = route.query.email
        companyName.value = route.query.teamName
        teamId.value = route.query.teamId
        signature.value = route.query.signature
        expire.value = route.query.expire
      }
    })

    /* METHODS */

    function uploadLogo (event) {
      const hasFiles = event.target.files.length

      if (!hasFiles) return

      const files = Array
        .from(event.target.files)
        .filter(file => (/image/i).test(file.type))

      if (files.length === 0) return

      const formData = new FormData()
      formData.append('data', files[0])

      createImage(files[0])

      file.value = formData
    }

    function createImage (file) {
      var reader = new FileReader()

      reader.onload = (e) => {
        image.value = e.target.result
      }
      reader.readAsDataURL(file)
    }

    const continueRegister = () => {
      requiredFieldsValidation(errorMessages, { companyName: companyName.value })

      if (Object.values(errorMessages).every(i => !i)) {
        step.value = 2
      }
    }

    const sendVerificationCode = () => {
      isDisabledCodeButton.value = true
      setTimeout(() => {
        isDisabledCodeButton.value = false
      }, 60000)

      commit('setNotification', { isVisible: true, message: 'Please check your email', color: 'success' })

      return teamsService.sendVerificationCode({ email: team.workEmail, teamName: companyName.value })
    }

    const registerTeamSecondStep = () => {
      requiredFieldsValidation(errorMessages, team)

      if (!errorMessages.workEmail) {
        errorMessages.workEmail = emailValidation(team.workEmail)
      }

      if (!errorMessages.password) {
        errorMessages.password = passwordValidation(team.password)
      }

      if (Object.values(errorMessages).every(i => !i)) {
        teamsService.checkEmail(team.workEmail).then(({ data }) => {
          hasPersonalAccount.value = data.hasPersonalAccount
          hasTeamAccount.value = data.hasTeamAccount

          if (hasTeamAccount.value) {
            errorMessages.workEmail = 'A team user already exists. '
            errorMessages.isNeedLink = true
            return
          } else {
            errorMessages.workEmail = null
            errorMessages.isNeedLink = false
          }
          if (hasPersonalAccount.value) {
            commit('setNotification', { isVisible: true, message: 'A user with this e-mail address already exists. Please login.', color: 'error' })
            loginModalRef.value.open()
            return
          }

          teamsService.sendVerificationCode({ email: team.workEmail, teamName: companyName.value })
            .then(() => {
              step.value = 3
            })
        })
      }
    }

    const toStep4 = () => {
      requiredFieldsValidation(errorMessages, account)

      if (Object.values(errorMessages).every(i => !i)) {
        step.value = 4
      }
    }

    const registerAndCreateUser = () => {
      requiredFieldsValidation(errorMessages, account)

      if (Object.values(errorMessages).every(i => !i)) {
        if (isInvite.value) {
          dispatch('acceptInviteAndCreateUser', {
            ...account,
            signature: signature.value,
            expire: expire.value,
            email: team.workEmail,
            teamId: +teamId.value,
            password: team.password
          }).then(() => {
            event('conversion', { send_to: 'AW-597169833/rbwSCLzh_OQCEKmt4JwC' })
            settingsService.update({ mode: 'Paper shape' })
            // window.fbq('trackCustom', 'signupTeams')
            router.push({ name: 'TeamBooks', params: { team: companyName.value } })
          })
        } else {
          dispatch('registerTeam', {
            ...account,
            email: team.workEmail,
            password: team.password,
            teamName: companyName.value
          }).then(async () => {
            event('conversion', { send_to: 'AW-597169833/rbwSCLzh_OQCEKmt4JwC' })
            settingsService.update({ mode: 'Paper shape' })
            // window.fbq('trackCustom', 'signupTeams')
            if (file.value) {
              setTimeout(() => commit('loading', true), 200)
              await teamsService.upload(file.value).then(res => commit('setLogo', res.data))
            }
            step.value = 5
          })
        }
      }
    }

    function register () {
      if (!isInvite.value) {
        dispatch('createTeam', companyName.value)
          .then(async ({ team }) => {
            teamId.value = team.id
            if (file.value) {
              setTimeout(() => commit('loading', true), 200)
              await teamsService.upload(file.value).then(res => commit('setLogo', res.data))
            }
            event('conversion', { send_to: 'AW-597169833/rbwSCLzh_OQCEKmt4JwC' })
            settingsService.update({ mode: 'Paper shape' })
            // window.fbq('trackCustom', 'signupTeams')
            step.value = 5
          })
      } else {
        dispatch('acceptInvite', {
          signature: signature.value,
          expire: expire.value,
          email: team.workEmail,
          teamId: +teamId.value
        }).then(() => {
          event('conversion', { send_to: 'AW-597169833/rbwSCLzh_OQCEKmt4JwC' })
          settingsService.update({ mode: 'Paper shape' })
          // window.fbq('trackCustom', 'signupTeams')
          router.push({ name: 'TeamBooks', params: { team: companyName.value } })
        })
      }
    }

    const checkConfirmCode = () => {
      if (confirmCode.value.length === 6 && !verification) {
        verification = true

        if (hasPersonalAccount.value) {
          register()
        } else {
          registerAndCreateUser()
        }
      }
    }

    const onLogin = () => {
      teamsService.sendVerificationCode({ email: team.workEmail, teamName: companyName.value })
        .then(() => {
          loginModalRef.value.close()
          step.value = 4
        })
    }

    function clearWorkEmailError (name) {
      errorMessages[name] = null
      errorMessages.isNeedLink = null
    }

    return {
      // refs
      companyNameRef,
      step,
      companyName,
      confirmCode,
      hasPersonalAccount,
      hasTeamAccount,
      isDisabledCodeButton,
      loginModalRef,
      teamId,
      image,
      // reactive
      errorMessages,
      team,
      account,
      // computed
      url,
      // function
      uploadLogo,
      continueRegister,
      registerTeamSecondStep,
      toStep4,
      checkConfirmCode,
      sendVerificationCode,
      clearWorkEmailError,
      onLogin
    }
  }
})
</script>
